/**
 * AddAssignmentDialog.tsx — Phase 3 Item 5, follow-up 1.6, follow-up 1.7.
 * Dialog for creating EmployeeAssignment rows (Task / Vacation / Sick / FieldWork / Other).
 * Decomposed into: AddAssignmentTaskFields, AddAssignmentDateRange, WeekChips, useVacationWeekData.
 *
 * Wave 3 (percent retirement, Q2A/Q3B): every percent field is GONE. A task
 * assignment is presence (busy on its covered days); ALL absence types
 * (Vacation / Sick / FieldWork / Other) are whole-day on their dated span.
 * Every payload writes Percent=100 (the server pins it anyway). A task landing
 * on a day off returns 409 absence_day_conflict — shown verbatim as a hard
 * error, with NO "Save anyway" (the old overbook confirm is retired).
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { AddAssignmentDateRange } from './AddAssignmentDateRange';
import type { DateRangeValue } from './AddAssignmentDateRange';
import { AddAssignmentTaskFields } from './AddAssignmentTaskFields';
import {
  weekOverloadHint,
  createEmployeeAssignment,
  createAbsenceBulk,
  type AbsenceWeekEntry,
} from '../../../services/scheduleAssignments';
import { fetchSkillMatrixUsers, type SkillUser } from '../../../services/scheduleSkillMatrix';
import { searchScheduleTasks, type TaskSearchResult } from '../../../services/scheduleTasks';
import { useVacationWeekData } from './useVacationWeekData';
import { computeVacationWeekSplits } from '../../../utils/vacationWeekSplits';
import { WeekChips, buildFallbackSplits } from './WeekChips';

// ── Types ───────────────────────────────────────────────────────────────────

export type AssignmentType = 'Task' | 'Vacation' | 'Sick' | 'FieldWork' | 'Other';

export interface AddAssignmentDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after all assignment rows have been created successfully. */
  /**
   * Success callback. G2-7: `overloadHint` is the non-blocking weekly-overload
   * advisory copy (weekOverloadHint) or null/undefined — parents with a
   * snackbar should surface it.
   */
  onSuccess: (overloadHint?: string | null) => void;
  /**
   * Pre-filled user (from the cell popup).
   * When null, the user picker is shown.
   */
  prefillUserKey?: string | null;
  prefillUserName?: string | null;
  /**
   * Pre-filled week start (from the cell popup).
   * For Vacation/Sick/FieldWork/Other, this sets the initial startDate.
   */
  prefillWeekStart?: string | null;
  /**
   * Phase 4, Item 7 — My Schedule: when true, the "Task" type option is hidden
   * and the assignment type defaults to "Vacation". The user picker is also
   * locked (prefillUserKey must be provided). Non-tech users may only add their
   * own absences via My Schedule.
   */
  restrictToAbsence?: boolean;
}

// ── Week boundary helpers (frontend-only) ──────────────────────────────────

function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + n));
  return dt.toISOString().slice(0, 10);
}

/** Return the Saturday that starts the BST week containing date. */
function weekStartFor(dateStr: string): string {
  if (!dateStr) return dateStr;
  const [y, m, d] = dateStr.split('-').map(Number);
  const dow = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
  const diff = dow === 6 ? 0 : -(dow + 1);
  return addDays(dateStr, diff);
}

/** Return list of week-start Saturdays that fully or partially overlap [start, end]. */
function weekStartsInRange(startDate: string, endDate: string): string[] {
  const starts: string[] = [];
  let cur = weekStartFor(startDate);
  while (cur <= endDate) {
    starts.push(cur);
    cur = addDays(cur, 7);
  }
  return starts;
}

function clamp(date: string, lo: string, hi: string): string {
  if (date < lo) return lo;
  if (date > hi) return hi;
  return date;
}

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

// ── Component ───────────────────────────────────────────────────────────────

export const AddAssignmentDialog: React.FC<AddAssignmentDialogProps> = ({
  open,
  onClose,
  onSuccess,
  prefillUserKey,
  prefillUserName,
  prefillWeekStart,
  restrictToAbsence = false,
}) => {
  const theme = useTheme();
  const today = todayISO();

  // ── State ──────────────────────────────────────────────────────────────────
  const [assignType, setAssignType] = useState<AssignmentType>(
    restrictToAbsence ? 'Vacation' : 'Task',
  );

  const [users, setUsers] = useState<SkillUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SkillUser | null>(null);

  // Task autocomplete (follow-up 1.7)
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [taskOptions, setTaskOptions] = useState<TaskSearchResult[]>([]);
  const [selectedTask, setSelectedTask] = useState<TaskSearchResult | null>(null);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const taskDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [dateRange, setDateRange] = useState<DateRangeValue>({
    startDate: prefillWeekStart ?? today,
    // Default to the week's Friday (Sat+6) — P2-1: the old +4 ended on Wednesday.
    endDate: prefillWeekStart ? addDays(prefillWeekStart, 6) : today,
  });
  const [taskWeekStart, setTaskWeekStart] = useState<string>(
    prefillWeekStart ?? weekStartFor(today),
  );
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // ── Load users on open ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return;
    setLoadingUsers(true);
    fetchSkillMatrixUsers()
      .then((list) => setUsers(list))
      .catch(() => setUsers([]))
      .finally(() => setLoadingUsers(false));
  }, [open]);

  // ── Pre-fill user from context ─────────────────────────────────────────────
  useEffect(() => {
    if (!open || !prefillUserKey || users.length === 0) return;
    const found = users.find((u) => u.AppUserKey === prefillUserKey);
    if (found) setSelectedUser(found);
  }, [open, prefillUserKey, users]);

  // ── Reset on close ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) return;
    setAssignType(restrictToAbsence ? 'Vacation' : 'Task');
    setSelectedUser(null);
    setSelectedTask(null);
    setTaskSearchQuery('');
    setTaskOptions([]);
    setLoadingTasks(false);
    setDateRange({
      startDate: prefillWeekStart ?? today,
      endDate: prefillWeekStart ? addDays(prefillWeekStart, 6) : today,
    });
    setTaskWeekStart(prefillWeekStart ?? weekStartFor(today));
    setNotes('');
    setSubmitError(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // ── Task autocomplete debounced search — follow-up 1.7 ────────────────────
  useEffect(() => {
    if (assignType !== 'Task' || taskSearchQuery.length < 2) {
      setTaskOptions([]);
      return;
    }
    // Debounce 300 ms
    if (taskDebounceRef.current) clearTimeout(taskDebounceRef.current);
    taskDebounceRef.current = setTimeout(() => {
      setLoadingTasks(true);
      searchScheduleTasks({ q: taskSearchQuery, aroundDate: taskWeekStart || undefined })
        .then((results) => setTaskOptions(results))
        .catch(() => setTaskOptions([]))
        .finally(() => setLoadingTasks(false));
    }, 300);

    return () => {
      if (taskDebounceRef.current) clearTimeout(taskDebounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskSearchQuery, assignType, taskWeekStart]);

  // ── Per-week working-days — follow-up 1.6 ────────────────────────────────
  const isVacationOrSick = assignType === 'Vacation' || assignType === 'Sick';
  const { perWeekData, loading: loadingDays, allHolidays: holidaysInRange } =
    useVacationWeekData(dateRange.startDate, dateRange.endDate, open && isVacationOrSick);
  const vacationSplits = isVacationOrSick
    ? computeVacationWeekSplits(dateRange.startDate, dateRange.endDate, perWeekData)
    : [];
  const workingDayCount = isVacationOrSick
    ? (perWeekData.get(weekStartFor(dateRange.startDate))?.workingDays ?? null)
    : null;

  // ── Build the per-week absence slices (one entry per overlapped week) ──────
  // A task = busy on its covered days; an absence = whole days off (Q2A). The
  // server pins Percent=100 and (for absences) ScenarioKey='BASELINE'.
  function buildAbsenceWeeks(): AbsenceWeekEntry[] {
    if (isVacationOrSick) {
      // Per-week split data (follow-up 1.6): each week keeps its own clamped
      // date span + working-day/holiday breakdown (the chips below).
      return vacationSplits.map((split) => ({
        WeekStart: split.weekStart,
        StartDate: split.effectiveStart,
        EndDate: split.effectiveEnd,
      }));
    }
    // FieldWork / Other: whole-day absence rows, one per overlapped week (Q2A —
    // the manual partial percent is retired; these read exactly like Vacation).
    const starts = weekStartsInRange(dateRange.startDate, dateRange.endDate);
    return starts.map((ws) => {
      const wFri = addDays(ws, 6);
      return {
        WeekStart: ws,
        StartDate: clamp(dateRange.startDate, ws, wFri),
        EndDate: clamp(dateRange.endDate, ws, wFri),
      };
    });
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  //
  // Task: ONE row for the picked week, spanning the week's WORKING days
  // (Mon–Fri of the Sat-anchored week — P2-1; the old +4 default ended the row
  // on WEDNESDAY, silently leaving Thu/Fri free in the busy-day ledger).
  //
  // Absence: ALL weeks go through the transactional absence-bulk endpoint
  // (P2-23) — everything saves or nothing does; retry never duplicates.
  //
  // A 409 absence_day_conflict (task on a day off) is a HARD block (Q3B): the
  // server's message shows verbatim in the error alert — there is deliberately
  // NO "Save anyway" path for it.
  const handleSubmit = async () => {
    setSubmitError(null);
    if (!selectedUser) {
      setSubmitError('Please select an employee.');
      return;
    }

    setSubmitting(true);
    try {
      let overloadHint: string | null = null;
      if (assignType === 'Task') {
        const created = await createEmployeeAssignment({
          UserKey: selectedUser.AppUserKey,
          WeekStart: taskWeekStart,
          ScheduleTaskKey: selectedTask?.ScheduleTaskKey ?? null,
          AbsenceType: null,
          Percent: 100,
          Source: 'Manual',
          Notes: notes || null,
          // The week's working days: Monday (Sat+2) through Friday (Sat+6).
          StartDate: addDays(taskWeekStart, 2),
          EndDate: addDays(taskWeekStart, 6),
        });
        // G2-7: non-blocking weekly-overload advisory from the 201 body.
        overloadHint = weekOverloadHint(created.weekOverloaded);
      } else {
        const weeks = buildAbsenceWeeks();
        if (weeks.length === 0) {
          setSubmitError('Date range produces no valid weeks.');
          return;
        }
        await createAbsenceBulk({
          UserKey: selectedUser.AppUserKey,
          AbsenceType: assignType as 'Vacation' | 'Sick' | 'FieldWork' | 'Other',
          Notes: notes || null,
          weeks,
        });
      }
      onSuccess(overloadHint);
      onClose();
    } catch (err) {
      // AbsenceDayConflictError carries the server message verbatim; other
      // errors fall back to their own message.
      setSubmitError(err instanceof Error ? err.message : 'Failed to save assignment.');
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Handle task selection in the autocomplete.
   * When a task is selected, pre-fill taskWeekStart from the task's StartDate
   * (clamped to the BST Saturday of that date). Follow-up 1.7.
   */
  const handleTaskChange = (task: TaskSearchResult | null) => {
    setSelectedTask(task);
    if (task?.StartDate) {
      setTaskWeekStart(weekStartFor(task.StartDate));
    }
  };

  // ── Derived ────────────────────────────────────────────────────────────────
  const isDateRangeType = assignType !== 'Task';

  const weekSpanCount = vacationSplits.length > 0
    ? vacationSplits.length
    : weekStartsInRange(dateRange.startDate, dateRange.endDate).length;

  // Derive the chip data for the vacation/sick per-week breakdown.
  const weekChipSplits = vacationSplits.length > 0
    ? vacationSplits
    : buildFallbackSplits(weekStartsInRange(dateRange.startDate, dateRange.endDate));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '10px' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
          <Typography variant="h6" fontWeight={700} sx={{ fontSize: '1rem' }}>
            Add Assignment
          </Typography>
        </Box>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
          Create a new employee assignment for one or more weeks.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 2 }}>

        {submitError && (
          <Alert severity="error" onClose={() => setSubmitError(null)} sx={{ py: 0.5 }}>
            {submitError}
          </Alert>
        )}

        {/* ── Assignment Type ──────────────────────────────────────── */}
        <Box>
          <Typography variant="caption" display="block" sx={{ mb: 0.75, fontWeight: 600 }}>
            Assignment Type
            {restrictToAbsence && (
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
                sx={{ ml: 1, fontWeight: 400 }}
              >
                (absence entries only)
              </Typography>
            )}
          </Typography>
          <ToggleButtonGroup
            value={assignType}
            exclusive
            onChange={(_, v) => { if (v) setAssignType(v as AssignmentType); }}
            size="small"
            sx={{ flexWrap: 'wrap', gap: 0.5 }}
          >
            {/* When restrictToAbsence is true, hide Task type (Phase 4 Item 7). */}
            {(['Task', 'Vacation', 'Sick', 'FieldWork', 'Other'] as const)
              .filter((t) => !(restrictToAbsence && t === 'Task'))
              .map((t) => (
                <ToggleButton
                  key={t}
                  value={t}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    px: 1.5,
                    py: 0.5,
                    borderRadius: '6px !important',
                    border: `1px solid ${theme.border.color} !important`,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark ?? theme.palette.primary.main,
                      },
                    },
                  }}
                >
                  {t === 'FieldWork' ? 'Field Work' : t}
                </ToggleButton>
              ))}
          </ToggleButtonGroup>
        </Box>

        {/* ── User picker ──────────────────────────────────────────── */}
        <Box>
          <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
            Employee
          </Typography>
          {prefillUserKey && prefillUserName ? (
            <Box
              sx={{
                px: 1.5, py: 0.75, borderRadius: '6px',
                border: `1px solid ${theme.border.color}`,
                backgroundColor: theme.table.stripeBg,
                display: 'inline-flex', alignItems: 'center',
              }}
            >
              <Typography variant="body2" fontWeight={600}>{prefillUserName}</Typography>
            </Box>
          ) : (
            <Autocomplete
              options={users}
              loading={loadingUsers}
              getOptionLabel={(u) => u.DisplayName}
              value={selectedUser}
              onChange={(_, v) => setSelectedUser(v)}
              size="small"
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search employees…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingUsers && <CircularProgress size={14} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}
        </Box>

        {/* ── Task-type-specific fields ────────────────────────────── */}
        {assignType === 'Task' && (
          <AddAssignmentTaskFields
            selectedTask={selectedTask}
            onTaskChange={handleTaskChange}
            taskSearchQuery={taskSearchQuery}
            onTaskSearchQueryChange={setTaskSearchQuery}
            taskOptions={taskOptions}
            loadingTasks={loadingTasks}
            weekStart={taskWeekStart}
            onWeekStartChange={setTaskWeekStart}
          />
        )}

        {/* ── Date range (non-Task types) ──────────────────────────── */}
        {isDateRangeType && (
          <Box>
            <Typography variant="caption" display="block" sx={{ mb: 0.75, fontWeight: 600 }}>
              Date Range (whole days — the person is off all day)
            </Typography>
            <AddAssignmentDateRange
              value={dateRange}
              onChange={setDateRange}
              holidaysInRange={holidaysInRange}
              workingDayCount={
                // For multi-week vacation/sick, suppress the single-value readout
                // (the per-week chips below show the correct per-week breakdown).
                isVacationOrSick && weekSpanCount > 1 ? null : workingDayCount
              }
              loadingDays={loadingDays}
            />

            {/* Per-week breakdown chips — follow-up 1.6 */}
            {weekSpanCount > 0 && isVacationOrSick && (
              <Box sx={{ mt: 1, px: 1.5, py: 0.75, borderRadius: '6px', backgroundColor: 'rgba(74,103,65,0.06)', border: `1px solid rgba(74,103,65,0.2)` }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', mb: 0.75, display: 'block' }}>
                  {weekSpanCount > 1 ? `${weekSpanCount} assignment rows (one per week):` : '1 assignment row:'}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  <WeekChips splits={weekChipSplits} />
                </Box>
              </Box>
            )}
            {/* Non-vacation multi-week summary */}
            {weekSpanCount > 1 && !isVacationOrSick && (
              <Box
                sx={{
                  mt: 1, px: 1.5, py: 0.75, borderRadius: '6px',
                  backgroundColor: 'rgba(74,103,65,0.06)',
                  border: `1px solid rgba(74,103,65,0.2)`,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                  This range spans <strong>{weekSpanCount} weeks</strong>.{' '}
                  Every working day in it is a full day off.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* ── Notes ────────────────────────────────────────────────── */}
        <Box>
          <Typography variant="caption" display="block" sx={{ mb: 0.5, fontWeight: 600 }}>
            Notes (optional)
          </Typography>
          <TextField
            multiline
            rows={2}
            size="small"
            fullWidth
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Any notes about this assignment…"
          />
        </Box>

      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          disabled={submitting}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => { void handleSubmit(); }}
          variant="contained"
          size="small"
          disabled={submitting || !selectedUser}
          startIcon={submitting ? <CircularProgress size={14} /> : <AddIcon />}
          sx={{ textTransform: 'none', ml: 1 }}
        >
          {submitting ? 'Saving…' : 'Add Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
